import {useEffect, useState} from "react";
import {useIdleTimer} from 'react-idle-timer';
import ConfirmDialog from "../confirmDialog";
import {LAG_TIMEOUT_SECONDS} from './constants';
import PropTypes from 'prop-types';
import "./style.css"
import {useDispatch, useSelector} from "react-redux";
import {getSessionTimeout, setLoadingStatus} from "../../../actions/session";


const SESSION_TIMER_ALPHA_DEFAULTS = {
    logoutSeconds: 300,
    warnSeconds: 120,
    pollSeconds: 60,
    pollUrl: null,
    exitUrl: null,
    exitAlert: null,
    exitAction: null,
    throttle: 1000,
}

/**
 * Reassigns the class of a specified element.
 *
 * @function reassignDivClass
 *
 * @param {string} elementId - The ID of the element to modify.
 * @param {string} classAdd - The class to add to the element.
 * @param {string} classRemove - The class to remove from the element.
 *
 * @returns {void}
 */
const reassignDivClass = (elementId, classAdd, classRemove) => {
    const element = document.getElementById(elementId);
    if(element) {
        element.classList.add(classAdd);
        element.classList.remove(classRemove);
    }
}

const debugLogger = (logData, isDebug) => {
    if(isDebug) {
        console.log(logData);
    }
}

const logTimer = (s, elapsed, isDebug) => {
    debugLogger(`[${s}] - ${elapsed/1000}`, isDebug);
}

/**
 * SessionTimer component that manages the session timeout functionality.
 *
 * https://ucwebdev.atlassian.net/browse/TR-78
 *
 * This will run across any page this is located in.
 *
 * Example:
 *
 * <SessionTimer logoutSeconds={30}
 *               warnSeconds={15}
 *               pollSeconds={2}
 *               exitUrl="https://exampple.com/API/logout"
 *               pollUrl-"https://example.com/API/poll
 * />
 *
 *  This Places a SessionTimer in the page.
 *      logoutSeconds: If no activities in the browser occur in {@link logoutSeconds}
 *      time, the browser will consider the session as idle.  This example will
 *      use {@link exitUrl} to logout the session.
 *
 *      warnSeconds: This is the threshold where the modalDiv will appear, and in the
 *      default timer will present a countdown.  If no click on the div occurs in time
 *      then a possible {@link timeoutDiv} may appear, or the exitUrl will be consumed.
 *
 *      pollSeconds: The frequency with which the timer should access the server-side
 *      API {@link pollUrl} to maintain the session activity.
 *
 *      modalDiv: The text name that will be used as the id of the modal contaiing the
 *      warning text.
 *
 *      exitUrl: The URL which the browser will go to if the session times out.
 *
 *      pollUrl: The URL which will be polled every {@link pollSeconds} to maintain the
 *      session while the user is active on the page.
 *
 * @param {Object} props - The component props.
 * @param {number} props.logoutSeconds - The time in seconds before the user is logged out.
 * @param {number} props.warnSeconds - The time in seconds before a warning is shown to the user.
 * @param {number} props.pollSeconds - The interval in seconds to poll the server.
 * @param {string} props.pollUrl - The URL to poll for server availability.
 * @param {string} props.exitUrl - The URL to redirect the user after session timeout.
 * @param {string} props.exitAlert - The text to display in the alert after session timeout.
 * @param {boolean} props.throttle - Flag to indicate if the polling should be throttled.
 * @returns {React.Component} - The SessionTimer component.
 */
const SessionTimer = props => {
    const {
        logoutSeconds, warnSeconds, pollSeconds,
        pollUrl, exitUrl, exitAlert, exitAction, throttle,
        disabled
    } = {...SESSION_TIMER_ALPHA_DEFAULTS, ...props};

    const dispatch = useDispatch();

    const [userState, setUserState] = useState('Active');
    const [count, setCount] = useState(0)
    const [remaining, setRemaining] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const [probeTime, setProbeTime] = useState(0);
    const [showExtendSessionModal, setShowExtendSessionModal] = useState(false);
    const [showExpiredSessionModal, setShowExpiredSessionModal] = useState(false);

    const isLoading = useSelector((state) => state.session.isLoading);


    //  If a need for other state tests are needed, add here
    const isIdleState = (state) => state === 'Idle';

    const changeStateElapsed = (newState, i) => {
        setUserState(newState);
        setElapsed(i.getElapsedTime());
    }

    const debugFlag = false;

    /**
     * Retrieves API results and returns a boolean value
     * indicating whether the results were successfully parsed.
     *
     * @returns {boolean} Returns true if the API results were successfully parsed, otherwise returns false.
     *
     */
    function getApiResults() {
        let parsePollResult = false;

        if (!disabled && !isLoading && pollSeconds && userState !== 'Idle') {
          dispatch(getSessionTimeout());
          parsePollResult = true;
        }
        return parsePollResult;
    }

    /**
     * Function to probe an API and check if it is available.  Attempts to
     * minimize probes from running too often by keeping a minimum
     * {@link LAG_TIMEOUT_SECONDS} seconds between API calls.
     *
     * @returns {boolean | null} - Returns true if the API passes.
     */
    const probeAPI = () => {
        if(isIdleState(userState)) {
            return false;
        }

        let now = Date.now();

        if (probeTime !== 0 && (now-probeTime) < LAG_TIMEOUT_SECONDS * 1000) {
          return null;
        }

        setProbeTime(now);

        if(!getApiResults()) {
          //  Server API failed, or returned a timeout for user
          setUserState('Idle');
          return false;
        }

        return true;
    }

    /**
     * Triggered when the user is considered idle.
     * This should be the end.  Either send them to
     * {@link exitUrl} or pop up an alert with the
     * text of {@link exitAlert}.
     *
     * It's over.
     *
     * @param {Event} e - The event object
     * @param {Object} i - The idleTimer object
     */
    const onIdle = (e,i) => {
        changeStateElapsed('Idle', i);

        //  Shut it down
        i.startManually=true;
        i.disabled=true;

        setShowExtendSessionModal(false);
        setShowExpiredSessionModal(false);

        const holdOnTime = 500;
        setTimeout(() => {
                setShowExpiredSessionModal(true);
                if (exitAction) {
                    exitAction();
                } else if(exitUrl) {
                    window.location.replace(exitUrl);
                }
            }, holdOnTime
        );
    }

    /**
     * Sets the state to 'Active' and updates the elapsed time.
     *
     * @param {Event} e - The event object
     * @param {Object} i - The idleTimer object
     */
    const onActive = (e,i) => {
        changeStateElapsed('Active', i);
    }

    /**
     * Keeps track of actions taking place on the page.
     * Currently just maintains "Active" state, but
     * could be used for activities on the page.
     *
     * Activities in e.type such as:
     *  'mousemove', 'keydown', 'wheel', etc.
     *
     * @param {Event} e - The event object.
     * @param {Object} i - The idleTimer object.
     */
    const onAction = (e,i) => {
        changeStateElapsed('Active', i);
        setCount(count+1);
    }

    /**
     * Called when the warning time comes into effect,
     * based on {@link warnSeconds}
     *
     * @param {Event} e - The event object.
     * @param {Object} i - The idleTimer object.
     */
    const onPrompt = (e,i) => {
        changeStateElapsed('Prompt', i);
        setShowExtendSessionModal(true);
    }

    const {getRemainingTime, getElapsedTime, activate} = useIdleTimer({
        onIdle, onActive, onAction, onPrompt,// onPresenceChange,
        timeout: logoutSeconds * 1000,
        promptBeforeIdle: warnSeconds * 1000,
        throttle: throttle,
        stopOnIdle: true,
        events: [
          "mousemove",
          "keydown",
          "scroll",
          "click"
        ]
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setRemaining(Math.ceil(getRemainingTime() / 1000))

            //  We've run out of time
            if(remaining <= 1) {
                clearInterval(interval);
                debugLogger("Stopping unless it's a file save and React restarts the component.", debugFlag)
                return;
            }

            //  We're checking if we need to check the API every second, but lag
            //  can always show up.
            let el = getElapsedTime();
            let checkSeconds = Math.floor(el/1000) % pollSeconds;

            if(checkSeconds < LAG_TIMEOUT_SECONDS) {
                let probe = probeAPI();
                if(probe !== null) {
                    setUserState('Idle');
                }
            }
        }, 1000);

        return () => {
            clearInterval(interval)
        }
    })

    /**
     * Handles the click event on continue button.
     *
     * @return {void}
     */
    const onConfirmContinueSession = () => {
      setUserState('Active');
      activate();
      probeAPI(); //  Keep the session active via API
      setShowExtendSessionModal(false);
    }

    const onExpiredSession = () => {
      setShowExpiredSessionModal(false);
    }

    return (
      <>
        <ConfirmDialog
          showModal={(showExtendSessionModal)}
          title='Session Expired Soon'
          confirmLabel='Continue'
          showCancelButton={true}
          message={`You will be logged off in <span class='timer-modal-bold'>${Math.floor(getRemainingTime() / 1000)}</span> seconds due to inactivity. Would you like to continue?`}
          onConfirm={onConfirmContinueSession}
        />

        <ConfirmDialog
          showModal={(showExpiredSessionModal)}
          title='Session Expired'
          confirmLabel='OK'
          showCancelButton={false}
          message={`You have been logged out from the server.`}
          onConfirm={onExpiredSession}
        />
    </>
    );
}

SessionTimer.propTypes = {
    logoutSeconds: PropTypes.number.isRequired, //300
    warnSeconds: PropTypes.number.isRequired,   //120
    pollSeconds: PropTypes.number.isRequired,//60
    pollUrl: PropTypes.string,
    exitUrl: PropTypes.string,
    exitAlert: PropTypes.string,
    exitAction: PropTypes.func,
    throttle: PropTypes.number, // 1000
};

export default SessionTimer;